// SubstitutionPanel.js :contentReference[oaicite:0]{index=0}
import React, { useState, useEffect } from "react";
import { useTheme } from "../config/ThemeContext";

const SubstitutionPanel = ({
  team,
  currentSet,
  playerMap,
  rotation,
  subs,
  matchId,
  socket,
  onClose,
  teamName,
  matchState,
}) => {
  const [selectedSubs, setSelectedSubs] = useState([]);
  const [exceptionalSubs, setExceptionalSubs] = useState({});
  const theme = useTheme();
  const allPlayers = Object.keys(playerMap[team] || {});
  const allowedLiberoIds = matchState.sets[currentSet].liberoIds[team] || [];
  const benchPlayers = allPlayers.filter((id) => {
  const isOnCourt = rotation.map(String).includes(id);
  const player = playerMap[team][id];
  const isLibero = player?.position?.toLowerCase() === "libero";

  // ❌ Exclude liberos not listed in liberoIds for this set
  if (isLibero && !allowedLiberoIds.includes(Number(id))) return false;

  return !isOnCourt;
});


const handleAddSub = (benchPlayerId) => {
  const benchPlayer = playerMap[team][benchPlayerId];
  const isBenchLibero =
    benchPlayer?.position?.toLowerCase() === "libero";

  if (selectedSubs.length >= 2) return;
  if (selectedSubs.some((sub) => sub.benchPlayerId === benchPlayerId)) return;

  setSelectedSubs((prev) => [
    ...prev,
    {
      benchPlayerId,
      courtPlayerId: rotation[0], // default, can still be changed by user
    },
  ]);
  setExceptionalSubs((prev) => ({
    ...prev,
    [benchPlayerId]: { isExceptional: false, reason: "" },
  }));
};


  const handleRemoveSub = (benchPlayerId) => {
    setSelectedSubs((prev) =>
      prev.filter((sub) => sub.benchPlayerId !== benchPlayerId)
    );
  };

  const handleCourtChange = (benchPlayerId, newCourtPlayerId) => {
    setSelectedSubs((prev) =>
      prev.map((sub) =>
        sub.benchPlayerId === benchPlayerId
          ? { ...sub, courtPlayerId: newCourtPlayerId }
          : sub
      )
    );
  };

  const confirmSubstitutions = () => {
    if (!socket || selectedSubs.length === 0) return;

const enrichedSubs = selectedSubs.map((sub) => ({
  ...sub,
  ...exceptionalSubs[sub.benchPlayerId],
}));

for (const { benchPlayerId, courtPlayerId } of selectedSubs) {
  const benchPlayer = playerMap[team][benchPlayerId];
  const courtPlayer = playerMap[team][courtPlayerId];

  const benchIsLibero = benchPlayer?.position?.toLowerCase() === "libero";
  const courtIsLibero = courtPlayer?.position?.toLowerCase() === "libero";

  if (benchIsLibero && courtIsLibero) {
    alert(
      `❌ Invalid substitution: Libero #${benchPlayer.shirtNumber} cannot replace Libero #${courtPlayer.shirtNumber}.`
    );
    return;
  }
}

for (const { benchPlayerId, courtPlayerId } of selectedSubs) {
  const benchPlayer = playerMap[team][benchPlayerId];
  const courtPlayer = playerMap[team][courtPlayerId];

  const benchIsLibero = benchPlayer?.position?.toLowerCase() === "libero";
  const courtIsLibero = courtPlayer?.position?.toLowerCase() === "libero";

  if (benchIsLibero && courtIsLibero) {
    alert(
      `❌ Invalid substitution: Libero #${benchPlayer.shirtNumber} cannot replace Libero #${courtPlayer.shirtNumber}.`
    );
    return;
  }
}


socket.emit("substitution", {
  matchId,
  currentSet,
  team,
  substitutions: enrichedSubs,
});


    onClose();
  };
  
    // Add ESC key to close panel
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div className="max-w-4xl mx-auto mt-6 bg-white border rounded-md shadow p-6">
      <h3 className="text-xl font-semibold mb-4 text-center text-gray-800">
        Substitution for {teamName}
      </h3>

      <p className="text-center font-medium text-sm mb-6">
        Substitutions used: <span className="font-bold">{subs}</span>
      </p>

      <div className="flex flex-col md:flex-row gap-6 justify-between">
        {/* Bench Players */}
        <div className="flex-1">
          <h4 className="text-md font-semibold mb-2">Bench Players</h4>
          <div className="space-y-2">
            {benchPlayers.map((id) => {
              const player = playerMap[team][id];
              const isLibero =
                player?.position?.toLowerCase() === "libero";
              // If libero, use state.libero from theme; otherwise, use team-specific colors.
              const benchButtonStyle = isLibero
                ? `${theme.state.libero.bg} hover:${theme.state.libero.hover} border ${theme.state.libero.border} ${theme.state.libero.text}`
                : `${theme[team].bg} hover:${theme[team].hover} border ${theme[team].border} ${theme[team].text}`;

              return (
                <button
                  key={id}
                  className={`w-full text-left px-4 py-2 rounded ${benchButtonStyle}`}
                  onClick={() => handleAddSub(id)}
				  title="Click to select"
                >
                  #{player.shirtNumber} - {player.shirtName}
                  {isLibero && (
                    <span className="ml-2 text-xs font-semibold">
                      (Libero)
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Substitution List */}
        <div className="flex-1">
          <h4 className="text-md font-semibold mb-2">Substitution(s)</h4>
          <div className="space-y-4">
            {selectedSubs.map(({ benchPlayerId, courtPlayerId }) => (
              <div key={benchPlayerId} className="flex flex-col gap-2">
                <div className="text-sm font-medium text-gray-700">
                  #{playerMap[team][benchPlayerId].shirtNumber}{" "}
                  {playerMap[team][benchPlayerId].shirtName} →
                </div>
                <select
                  className={`px-2 py-1 rounded border ${theme[team].border} ${theme[team].text} ${theme[team].focusRing}`}
                  value={courtPlayerId}
                  onChange={(e) =>
                    handleCourtChange(benchPlayerId, e.target.value)
                  }
                >
                  {rotation.map((id) => (
                    <option key={id} value={id}>
                      #{playerMap[team][id]?.shirtNumber} -{" "}
                      {playerMap[team][id]?.shirtName}
                    </option>
                  ))}
                </select>
                                <label className="inline-flex items-center gap-2 mt-1 text-sm">
                  <input
                    type="checkbox"
                    checked={exceptionalSubs[benchPlayerId]?.isExceptional || false}
                    onChange={(e) =>
                      setExceptionalSubs((prev) => ({
                        ...prev,
                        [benchPlayerId]: {
                          ...prev[benchPlayerId],
                          isExceptional: e.target.checked,
                          reason: e.target.checked
                            ? prev[benchPlayerId]?.reason || "Injury"
                            : "",
                        },
                      }))
                    }
                    className="accent-blue-600"
                  />
                  Mark as Exceptional
                </label>

                {exceptionalSubs[benchPlayerId]?.isExceptional && (
                  <select
                    value={exceptionalSubs[benchPlayerId]?.reason || ""}
                    onChange={(e) =>
                      setExceptionalSubs((prev) => ({
                        ...prev,
                        [benchPlayerId]: {
                          ...prev[benchPlayerId],
                          reason: e.target.value,
                        },
                      }))
                    }
                    className={`mt-1 px-2 py-1 rounded border ${theme[team].border} ${theme[team].text} ${theme[team].focusRing}`}
                  >
                    <option value="Injury">Injury</option>
                    <option value="Sanction">Sanction/Penalty</option>
                    <option value="Illness">Illness</option>
                    <option value="Libero Injury">Libero Injury</option>
                    <option value="Other">Other</option>
                  </select>
                )}

                <button
                  className={`text-sm self-start px-2 py-1 rounded ${theme.state.danger.text} hover:underline`}
                  onClick={() => handleRemoveSub(benchPlayerId)}
                >
                  ✖ Remove
                </button>

              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="mt-6 flex justify-end gap-4">
        <button
		  className={`${theme.buttons.primary} focus:outline-none`}
          onClick={confirmSubstitutions}
        >
          ✅ Confirm Substitution
        </button>
        <button
			className={`${theme.buttons.tertiary} focus:outline-none`}
          onClick={onClose}
        >
          ❌ Cancel
        </button>
      </div>
    </div>
  );
};

export default SubstitutionPanel;
